// Shipments service - moved from database.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../../firebase';

// Import payroll service for commission processing
import { payrollService } from '../accounting/payroll';
import { vehiclesService } from './vehicles';
import cache from '../../utils/dataCache';
import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';

const tripLabel = (s) => s?.trackingNumber || s?.tripNumber || s?.shipmentNumber || s?.id || 'trip';

// Note: These imports will be updated once all services are moved
// import { companySettingsService } from '../system/company-settings';
// import { clientsService } from './clients';
// import { invoicesService } from '../accounting/invoices';

export const shipmentsService = {
  async getShipments(companyId) {
    const key = `shipments:${companyId}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const shipmentsRef = collection(db, 'shipments');
    const q = query(shipmentsRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    cache.set(key, data);
    return data;
  },
  async getActiveShipments(companyId) {
    const key = `shipments_active:${companyId}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const shipmentsRef = collection(db, 'shipments');
    const [pendingSnap, transitSnap] = await Promise.all([
      getDocs(query(shipmentsRef, where('companyId', '==', companyId), where('status', '==', 'pending'))),
      getDocs(query(shipmentsRef, where('companyId', '==', companyId), where('status', '==', 'in_transit'))),
    ]);
    const data = [
      ...pendingSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      ...transitSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    ];
    cache.set(key, data, 60000); // 60s TTL — short so new assignments reflect quickly
    return data;
  },
  async getShipment(shipmentId, companyId) {
    const shipmentRef = doc(db, 'shipments', shipmentId);
    const snapshot = await getDoc(shipmentRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const shipmentData = snapshot.data();
    
    // Verify shipment belongs to the correct company
    if (companyId && shipmentData.companyId !== companyId) {
      throw new Error('Shipment not found or access denied');
    }
    
    return { id: snapshot.id, ...shipmentData };
  },
  // Generate a human-friendly shipment number in the format YYMMDDNNN,
  // where NNN is a 3-digit sequential counter that resets each day per company.
  // Uses a Firestore transaction so concurrent creates don't collide.
  async generateShipmentNumber(companyId) {
    if (!companyId) {
      throw new Error('companyId is required to generate a shipment number');
    }
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const datePart = `${yy}${mm}${dd}`;

    const counterRef = doc(db, 'shipmentCounters', `${companyId}_${datePart}`);
    const nextNum = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const current = snap.exists() ? (snap.data().count || 0) : 0;
      const next = current + 1;
      tx.set(counterRef, {
        companyId,
        date: datePart,
        count: next,
        updatedAt: serverTimestamp()
      });
      return next;
    });
    return `${datePart}${String(nextNum).padStart(3, '0')}`;
  },
  // Atomically generates the shipment number AND creates the shipment doc
  // in a single transaction. The counter only advances if the shipment is
  // actually saved, eliminating gaps caused by failed creates or double-clicks.
  async addShipmentWithGeneratedNumber(shipmentData) {
    if (!shipmentData?.companyId) {
      throw new Error('companyId is required');
    }
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const datePart = `${yy}${mm}${dd}`;

    const counterRef = doc(db, 'shipmentCounters', `${shipmentData.companyId}_${datePart}`);
    const newShipmentRef = doc(collection(db, 'shipments'));

    const createdAtValue = shipmentData.pickupDate
      ? Timestamp.fromDate(new Date(shipmentData.pickupDate))
      : Timestamp.fromDate(now);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const current = snap.exists() ? (snap.data().count || 0) : 0;
      const next = current + 1;
      const trackingNumber = `${datePart}${String(next).padStart(3, '0')}`;

      const dataToSave = {
        ...shipmentData,
        trackingNumber,
        createdAt: createdAtValue,
        // Real wall-clock creation time. `createdAt` is forced to the pickup
        // date for business-date sorting elsewhere, so we keep a separate
        // field for ledger / chronological ordering within the same day.
        recordedAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        status: shipmentData.status || 'pending',
      };

      tx.set(counterRef, {
        companyId: shipmentData.companyId,
        date: datePart,
        count: next,
        updatedAt: Timestamp.fromDate(now),
      });
      tx.set(newShipmentRef, dataToSave);

      return { id: newShipmentRef.id, trackingNumber, dataToSave };
    });

    cache.invalidatePrefix('shipments:');
    auditService.log({
      module: AUDIT_MODULES.TRIP,
      action: AUDIT_ACTIONS.CREATE,
      recordId: result.id,
      recordLabel: tripLabel(result.dataToSave),
      after: result.dataToSave,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.TRIP_CHANGES],
      companyId: shipmentData.companyId,
      meta: {
        tripNumber: result.dataToSave.trackingNumber,
        trackingNumber: result.dataToSave.trackingNumber,
        customerId: result.dataToSave.clientId,
        driverId: result.dataToSave.driverId
      }
    });

    return { id: result.id, trackingNumber: result.trackingNumber };
  },
  async addShipment(shipmentData) {
    const shipmentsRef = collection(db, 'shipments');
    
    // Use pickupDate as createdAt for business logic sorting
    const createdAtValue = shipmentData.pickupDate ? 
      Timestamp.fromDate(new Date(shipmentData.pickupDate)) : 
      serverTimestamp();
    
    const dataToSave = {
      ...shipmentData,
      createdAt: createdAtValue,
      recordedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: shipmentData.status || 'pending'
    };
    
    const docRef = await addDoc(shipmentsRef, dataToSave);
    cache.invalidatePrefix('shipments:');
    auditService.log({
      module: AUDIT_MODULES.TRIP,
      action: AUDIT_ACTIONS.CREATE,
      recordId: docRef.id,
      recordLabel: tripLabel(dataToSave),
      after: dataToSave,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.TRIP_CHANGES],
      companyId: shipmentData.companyId,
      meta: {
        tripNumber: dataToSave.trackingNumber,
        trackingNumber: dataToSave.trackingNumber,
        customerId: dataToSave.clientId,
        driverId: dataToSave.driverId
      }
    });
    return docRef.id;
  },
  async updateShipment(shipmentId, shipmentData) {
    const shipmentRef = doc(db, 'shipments', shipmentId);
    
    // Get current shipment to check for status changes
    const currentShipment = await getDoc(shipmentRef);
    const currentData = currentShipment.exists() ? currentShipment.data() : null;
    
    // Prepare update data
    const updateData = { ...shipmentData, updatedAt: serverTimestamp() };
    
    // If pickupDate changed, update createdAt to reflect the new business date
    if (shipmentData.pickupDate && currentData && currentData.pickupDate !== shipmentData.pickupDate) {
      console.log('🔄 Pickup date changed, updating createdAt for business logic sorting');
      updateData.createdAt = Timestamp.fromDate(new Date(shipmentData.pickupDate));
    }
    
    await updateDoc(shipmentRef, updateData);
    cache.invalidatePrefix('shipments:');

    auditService.logUpdate({
      module: AUDIT_MODULES.TRIP,
      recordId: shipmentId,
      recordLabel: tripLabel({ ...currentData, ...shipmentData }),
      before: currentData,
      after: { ...(currentData || {}), ...shipmentData },
      extraTags: [AUDIT_TAGS.TRIP_CHANGES],
      meta: {
        tripNumber: currentData?.trackingNumber || shipmentData.trackingNumber,
        trackingNumber: currentData?.trackingNumber || shipmentData.trackingNumber,
        customerId: currentData?.clientId || shipmentData.clientId,
        driverId: currentData?.driverId || shipmentData.driverId
      }
    });

    // Check if shipment was just marked as delivered
    if (currentData && 
        currentData.status !== 'delivered' && 
        shipmentData.status === 'delivered') {
      
      try {
        // Process commission for the driver
        console.log('🔄 Processing commission for delivered shipment:', shipmentId);
        await payrollService.processShipmentCommission(shipmentId, currentData.companyId);
        
        // Auto-create invoice if enabled and has cost
        if (shipmentData.cost > 0) {
          // Note: This will be updated once companySettingsService is moved
          // Check company settings for auto-invoice on delivery
          // const companySettings = await companySettingsService.getCompanySettings(currentData.companyId);
          // if (companySettings?.autoInvoiceOnDelivery) {
          //   console.log('🔄 Auto-creating invoice for delivered shipment:', shipmentId);
          //   await this.autoCreateInvoiceForShipment(shipmentId, currentData, shipmentData);
          // }
        }
      } catch (error) {
        console.error('❌ Error processing shipment delivery:', error);
        // Don't fail the shipment update if commission/invoice processing fails
      }
    }
    
    return true;
  },

  async autoCreateInvoiceForShipment(shipmentId, currentData, shipmentData) {
    try {
      // Note: These will be updated once services are moved
      // Get client information
      // const client = await clientsService.getClient(currentData.clientId);
      // if (!client) {
      //   throw new Error('Client not found for shipment');
      // }

      // Generate invoice number
      // const invoiceNumber = invoicesService.generateInvoiceNumber();
      
      // Calculate totals
      const subtotal = shipmentData.cost || currentData.cost || 0;
      const vatAmount = subtotal * 0.15;
      const total = subtotal + vatAmount;
      
      const invoiceData = {
        // invoiceNumber,
        companyId: currentData.companyId,
        clientId: currentData.clientId,
        shipmentIds: [shipmentId],
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        description: `Auto-generated invoice for shipment ${currentData.trackingNumber || shipmentId}`,
        notes: 'Automatically created upon shipment delivery',
        subtotal: Math.round(subtotal * 100) / 100,
        vatAmount: Math.round(vatAmount * 100) / 100,
        vatRate: 0.15,
        finalTotal: Math.round(total * 100) / 100,
        total: Math.round(total * 100) / 100,
        status: 'sent', // Auto-send the invoice
        paymentStatus: 'unpaid',
        paidAmount: 0,
        // client: {
        //   id: client.id,
        //   name: client.name || client.companyName,
        //   email: client.email,
        //   phone: client.phone,
        //   address: client.address
        // },
        items: [{
          description: `Shipment ${currentData.trackingNumber || shipmentId} - ${currentData.origin || currentData.fromLocation} to ${currentData.destination || currentData.toLocation}`,
          comments: (() => {
            const serviceType = currentData.serviceType || 'Standard';
            const truckType = currentData.truckType;
            const truckInfo = truckType ? `- ${truckType}` : '';
            return `Service: ${serviceType}${truckInfo}, Delivered: ${new Date().toLocaleDateString()}`;
          })(),
          quantity: 1,
          unitPrice: subtotal,
          total: subtotal,
          shipmentId: shipmentId
        }],
        createdBy: shipmentData.updatedBy || currentData.createdBy,
        autoGenerated: true
      };

      console.log('🔄 Creating auto-invoice with data:', invoiceData);
      // const invoiceId = await invoicesService.addInvoice(invoiceData);
      console.log('✅ Auto-invoice created successfully:', 'invoiceId');
      
      // return invoiceId;
    } catch (error) {
      console.error('❌ Error in autoCreateInvoiceForShipment:', error);
      throw error;
    }
  },
  async deleteShipment(shipmentId) {
    const shipmentRef = doc(db, 'shipments', shipmentId);
    let before = null;
    try {
      const snap = await getDoc(shipmentRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await deleteDoc(shipmentRef);
    cache.invalidatePrefix('shipments:');

    auditService.log({
      module: AUDIT_MODULES.TRIP,
      action: AUDIT_ACTIONS.DELETE,
      recordId: shipmentId,
      recordLabel: tripLabel(before),
      before,
      after: null,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.TRIP_CHANGES, AUDIT_TAGS.DELETIONS],
      companyId: before?.companyId,
      meta: {
        tripNumber: before?.trackingNumber,
        trackingNumber: before?.trackingNumber,
        customerId: before?.clientId,
        driverId: before?.driverId
      }
    });
    return true;
  },
  
  // Enhanced invoice relationship tracking
  async getAvailableShipments(companyId) {
    const shipmentsRef = collection(db, 'shipments');
    const q = query(shipmentsRef, where('companyId', '==', companyId), where('status', '==', 'delivered'));
    const snapshot = await getDocs(q);
    
    const shipments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Get all invoices to check relationships
    const invoicesRef = collection(db, 'invoices');
    const invoicesQuery = query(invoicesRef, where('companyId', '==', companyId));
    const invoicesSnapshot = await getDocs(invoicesQuery);
    const invoices = invoicesSnapshot.docs.map(doc => {
      const data = doc.data();
      return { 
        ...data, 
        id: data.id || doc.id,  // Preserve formatted invoice number from document data, fallback to doc ID
        docId: doc.id  // Keep the Firebase document ID as a separate field if needed
      };
    });

    // Load vehicles to get truck type information
    let vehicles = [];
    try {
      vehicles = await vehiclesService.getVehicles(companyId);
    } catch (error) {
      console.error('Error loading vehicles for truck type info:', error);
    }
    
    // Enhanced shipment availability logic with truck type information
    return shipments.map(shipment => {
      const relatedInvoices = invoices.filter(invoice => 
        invoice.shipmentIds && invoice.shipmentIds.includes(shipment.id)
      );
      
      const hasNonDraftInvoice = relatedInvoices.some(invoice => 
        invoice.status && invoice.status !== 'draft' && invoice.status !== 'cancelled'
      );
      
      const draftInvoices = relatedInvoices.filter(invoice => 
        invoice.status === 'draft'
      );
      
      const invoiceHistory = relatedInvoices.map(invoice => ({
        invoiceId: invoice.id,
        status: invoice.status,
        createdAt: invoice.createdAt,
        total: invoice.total
      }));

      // Get truck type information
      let truckType = null;
      if (shipment.isExternalDriver && shipment.truckType) {
        // For external drivers, use the truck type from shipment
        truckType = shipment.truckType;
      } else if (shipment.vehicleId) {
        // For internal vehicles, get truck type from vehicle
        const vehicle = vehicles.find(v => 
          v.id === shipment.vehicleId || 
          v.id === shipment.vehicleId?.toString() ||
          v.id?.toString() === shipment.vehicleId?.toString()
        );
        if (vehicle) {
          truckType = vehicle.truckType || vehicle.type;
        }
      }
      
      return {
        ...shipment,
        truckType: truckType, // Add truck type to shipment data
        invoiceStatus: hasNonDraftInvoice ? 'invoiced' : (draftInvoices.length > 0 ? 'draft' : 'available'),
        isAvailable: !hasNonDraftInvoice, // Available if no non-draft invoices
        relatedInvoices: invoiceHistory,
        currentInvoice: relatedInvoices.find(inv => inv.status !== 'cancelled') || null
      };
    });
  },
  
  async updateShipmentInvoiceStatus(shipmentId, invoiceId, invoiceStatus) {
    const shipmentRef = doc(db, 'shipments', shipmentId);
    await updateDoc(shipmentRef, { 
      invoiceId, 
      invoiceStatus, 
      updatedAt: serverTimestamp() 
    });
    return true;
  },
  
  async clearShipmentInvoiceStatus(shipmentId) {
    const shipmentRef = doc(db, 'shipments', shipmentId);
    await updateDoc(shipmentRef, { 
      invoiceId: null, 
      invoiceStatus: null, 
      invoiced: false,
      updatedAt: serverTimestamp() 
    });
    return true;
  }
}; 